import { yupResolver } from '@hookform/resolvers/yup';
import Add from '@mui/icons-material/Add';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Order_Read_Typeahead } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { FeatureFlags } from '~constants/featureFlags';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { FreightRateErrorMap } from './applySiteRateSaveResult';
import FreightRateItem from './FreightRateItem';
import RateChangeImpactDrawer, {
  type RateChangeImpactConfirmResult,
} from './RateChangeImpactDrawer';
import {
  createDefaultSiteRateScheduleDTO,
  FREIGHT_RATE_COVERAGE_ERROR_PATH,
  hasCustomerFreightRate,
  makeSiteRatesSchema,
  pickInheritedCommission,
  SiteRateScheduleDTO,
} from './SiteRatesForm';
import { resolveUseVendorRateForDriver } from './vendorRateForDriver';

interface FreightRatesManagerProps {
  siteRates: SiteRateScheduleDTO[];
  setSiteRates: (rates: SiteRateScheduleDTO[]) => void;
  isLoading: boolean;
  isCopyAndEdit?: boolean;
  accountId?: string;
  projectId?: string;
  onDirtyStatusChange?: () => void;
  prevailingWage?: boolean;
  validateRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  defaultSiteRateScheduleId?: string | null;
  onDefaultSiteRateScheduleChange?: (id: string | null) => void;
  // Per-card errors keyed by clientKey. When the parent applies
  // a partial-failure save result it bumps `resetGeneration`, prompting us
  // to re-seed the form from `siteRates`. `onClearError` is called when
  // the user edits any field on a row, so the inline error disappears
  // until the next failed submit.
  errorsByClientKey?: FreightRateErrorMap;
  resetGeneration?: number;
  onClearError?: (clientKey: string) => void;
  // 'order' = a single order's rate cards; 'project' = a project's rate cards.
  // Drives the delete-confirmation copy: order-level reuses the
  // unstar message; project-level fetches non-completed orders that have this
  // SRS as their default and lists them in the confirmation modal.
  scope?: 'order' | 'project';
  companyId?: string;
  // The order's service date (`YYYY-MM-DD`), forwarded to each card's
  // fuel-surcharge rows so they resolve at the date billing will use.
  // Absent on the project form, whose rows show today's rate.
  orderDate?: string;
  // The project's persisted toggle for selective rate apply. When ON (project
  // scope), a card delete is gated by the MERGED save-time impact dialog in FreightRates
  // (the row funnels into `toDelete` and is previewed there with the cascade-reachable
  // audience) — so the click-time read-only affected-orders confirmation is skipped to
  // avoid showing two dialogs for one save. When OFF/undefined, the legacy
  // delete confirmation is preserved unchanged.
  applyRateChangesToExistingOrders?: boolean;
  isFreightRateRequired?: boolean;
}

// Star, unstar, and delete-while-starred all move job rates downstream, so all
// three are gated by the same confirmation dialog. The dialog renders distinct
// copy per type.
//
// Deletion variants key off `clientKey` (the stable per-row identity)
// rather than the field-array index. The parent may re-seed
// `siteRates` between the click and the confirm (e.g. after a partial-failure
// save applies its result), which would shift the index out from under us and
// cause `performDelete` to mark the wrong row deleted. Resolving the current
// index from `clientKey` at confirm time is robust against that race.
// PostHog `field` values for `rate_card_field_edited`, keyed by the
// `siteRates.<index>.<segment>` form path that changed. Segments not listed
// (add-ons, prevailing-wage fields, useVendorRateForDriver, isDeleted, …)
// are deliberately untracked.
const TRACKED_RATE_CARD_FIELDS: Record<string, string> = {
  name: 'name',
  customerRate: 'customer_rate',
  vendorRate: 'vendor_rate',
  driverRate: 'driver_rate',
  pickupSite: 'pickup_site',
  dropoffSite: 'dropoff_site',
  equipmentType: 'equipment_type',
  material: 'material',
};

type PendingRateChange =
  | { type: 'star'; rateId: string }
  | { type: 'unstar' }
  | { type: 'delete-order-default'; clientKey: string }
  | {
      type: 'delete-project';
      clientKey: string;
      affectedOrders: Order_Read_Typeahead[];
    };

const FreightRatesManager = (props: FreightRatesManagerProps) => {
  // Gate the form on Split SDK readiness so the
  // `default_vendor_rate_to_driver` feature flag value is correct on the
  // first (and only) render that calls `useForm` / runs the reset effect.
  // Without this gate, `useFeatureFlag` returns `false` until the SDK is
  // ready, and the `hasResetFromPropsRef` guard would lock that wrong
  // value in even after the SDK becomes ready.
  const { isReady, isFeatureFlagEnabled: defaultVendorRateToDriverFeatureFlagEnabled } =
    useAwaitableFeatureFlag({
      featureFlagKey: FeatureFlags.defaultVendorRateToDriver,
    });

  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FreightRatesManagerInner
      {...props}
      defaultVendorRateToDriverFeatureFlagEnabled={
        defaultVendorRateToDriverFeatureFlagEnabled
      }
    />
  );
};

interface FreightRatesManagerInnerProps extends FreightRatesManagerProps {
  defaultVendorRateToDriverFeatureFlagEnabled: boolean;
}

const FreightRatesManagerInner = ({
  siteRates,
  setSiteRates,
  isLoading,
  isCopyAndEdit,
  accountId,
  projectId,
  onDirtyStatusChange,
  prevailingWage,
  validateRef,
  defaultSiteRateScheduleId,
  onDefaultSiteRateScheduleChange,
  errorsByClientKey,
  resetGeneration,
  onClearError,
  scope = 'order',
  companyId,
  orderDate,
  applyRateChangesToExistingOrders,
  isFreightRateRequired = false,
  defaultVendorRateToDriverFeatureFlagEnabled,
}: FreightRatesManagerInnerProps) => {
  const theme = useTheme();
  const { getSiteRateScheduleDefaultOnOrders } = useSiteRateSchedule();
  const resolveDefault = useCallback(
    (rate: SiteRateScheduleDTO): boolean =>
      resolveUseVendorRateForDriver(rate, defaultVendorRateToDriverFeatureFlagEnabled),
    [defaultVendorRateToDriverFeatureFlagEnabled],
  );

  const siteRatesResolver = useMemo(
    () => yupResolver(makeSiteRatesSchema(isFreightRateRequired)),
    [isFreightRateRequired],
  );
  const {
    control,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm<FieldValues>({
    resolver: siteRatesResolver,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      siteRates:
        siteRates.length > 0
          ? siteRates.map((rate) => ({
              isDeleted: rate.isDeleted || false,
              id: rate.id,
              clientKey: rate.clientKey,
              name: rate.name ?? null,
              pickupSite: rate.pickupSite,
              dropoffSite: rate.dropoffSite,
              customerRate: rate.customerRate,
              vendorRate: rate.vendorRate,
              vendorPayCommissionPercentage: rate.vendorPayCommissionPercentage,
              driverPayCommissionPercentage: rate.driverPayCommissionPercentage,
              ...pickInheritedCommission(rate),
              netVendorRate: rate.netVendorRate,
              netDriverRate: rate.netDriverRate,
              driverRate: rate.driverRate,
              customerAddOns: rate.customerAddOns || [],
              vendorAddOns: rate.vendorAddOns || [],
              driverAddOns: rate.driverAddOns || [],
              material: rate.material ?? null,
              equipmentType: rate.equipmentType ?? null,
              useVendorRateForDriver: resolveDefault(rate),
              jurisdiction: rate.jurisdiction ?? null,
              quantity: rate.quantity ?? undefined,
              pwOnSiteRate: rate.pwOnSiteRate ?? undefined,
              totalFringeRate: rate.totalFringeRate ?? undefined,
            }))
          : [
              createDefaultSiteRateScheduleDTO(
                defaultVendorRateToDriverFeatureFlagEnabled,
              ),
            ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'siteRates',
  });

  useEffect(() => {
    if (validateRef) {
      validateRef.current = () => trigger();
    }
    return () => {
      if (validateRef) {
        validateRef.current = null;
      }
    };
  }, [trigger, validateRef]);

  const handleAddFreightRule = useCallback(() => {
    append(createDefaultSiteRateScheduleDTO(defaultVendorRateToDriverFeatureFlagEnabled));
    AnalyticsController.track('rate_card_added', { scope });
  }, [append, defaultVendorRateToDriverFeatureFlagEnabled, scope]);

  // Suppress watch→setSiteRates feedback during form.reset()
  const isResettingRef = useRef(false);
  const hasResetFromPropsRef = useRef(false);

  // Sync the FF-resolved form state to the parent on mount. The watch
  // subscription only pushes to the parent on user changes, so without
  // this push the parent's `siteRates` would retain whatever convertToSiteRateDTO
  // produced (notably `useVendorRateForDriver: undefined` for server-loaded
  // rate cards), breaking save-time `deriveDriverFromVendor` in
  // FreightRates.makeOperationsList.
  //
  // Skip the push when the form mounted with no rates: the parent fetches
  // SRSs asynchronously, and pushing the local `[createDefault]` would race
  // with the incoming fetch result, causing the reset effect below to lock
  // onto the empty default and never see the real SRSs (TRE-14409).
  useEffect(() => {
    if (siteRates.length > 0) {
      setSiteRates(getValues('siteRates') as SiteRateScheduleDTO[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Reset the form once when the parent provides fetched data (async load).
    // After the initial reset we never reset again — the form owns the state.
    if (!hasResetFromPropsRef.current && siteRates.length > 0) {
      // Check if any rate has an id, meaning it came from the server
      const hasServerData = siteRates.some((r) => Boolean(r.id));
      const hasCopiedData = isCopyAndEdit && siteRates.length > 0;
      if (hasServerData || hasCopiedData) {
        hasResetFromPropsRef.current = true;
        isResettingRef.current = true;
        const resolvedRates = siteRates.map((rate) => ({
          isDeleted: rate.isDeleted || false,
          id: rate.id,
          clientKey: rate.clientKey,
          name: rate.name ?? null,
          pickupSite: rate.pickupSite,
          dropoffSite: rate.dropoffSite,
          customerRate: rate.customerRate,
          vendorRate: rate.vendorRate,
          vendorPayCommissionPercentage: rate.vendorPayCommissionPercentage,
          driverPayCommissionPercentage: rate.driverPayCommissionPercentage,
          ...pickInheritedCommission(rate),
          netVendorRate: rate.netVendorRate,
          netDriverRate: rate.netDriverRate,
          driverRate: rate.driverRate,
          customerAddOns: rate.customerAddOns || [],
          vendorAddOns: rate.vendorAddOns || [],
          driverAddOns: rate.driverAddOns || [],
          material: rate.material ?? null,
          equipmentType: rate.equipmentType ?? null,
          useVendorRateForDriver: resolveDefault(rate),
          jurisdiction: rate.jurisdiction ?? null,
          quantity: rate.quantity ?? undefined,
          pwOnSiteRate: rate.pwOnSiteRate ?? undefined,
          totalFringeRate: rate.totalFringeRate ?? undefined,
        }));
        reset({ siteRates: resolvedRates });
        // Push the resolved values to the parent too — `watch` is suppressed
        // during the reset, so without this the parent's siteRates would
        // keep the unresolved values it received from convertToSiteRateDTO.
        setSiteRates(resolvedRates);
        // Allow the watch callback to resume on the next tick
        requestAnimationFrame(() => {
          isResettingRef.current = false;
        });
      }
    }
  }, [siteRates, reset, isCopyAndEdit, resolveDefault, setSiteRates]);

  // Post-save force-reset. The parent (`FreightRates`) calls
  // `applySaveResult` after a partial-failure submit, mutates `siteRates`
  // (server IDs stamped, settled add-ons cleared, successful deletes
  // removed), and bumps `resetGeneration`. We re-seed the form so the
  // user sees the post-save state without losing their unsaved edits on
  // failed rows.
  const lastResetGenerationRef = useRef(resetGeneration ?? 0);
  useEffect(() => {
    if (resetGeneration === undefined) return;
    if (resetGeneration === lastResetGenerationRef.current) return;
    lastResetGenerationRef.current = resetGeneration;

    isResettingRef.current = true;
    reset({ siteRates: siteRates as unknown as FieldValues['siteRates'] });
    requestAnimationFrame(() => {
      isResettingRef.current = false;
    });
  }, [resetGeneration, siteRates, reset]);

  // Tracks `rate_card_field_edited` once per (rate card, field) per mount:
  // the answer "did the user touch this field on this card" needs one event,
  // and keystroke-level repeats would only flood PostHog.
  const trackedFieldEditsRef = useRef(new Set<string>());

  // Clears inline errors on edit. Watch fires with `name`
  // on user edits; we extract the rate index, look up its clientKey, and
  // ask the parent to drop that row's error bundle. Errors stay cleared
  // until the next failed submit re-populates them.
  useEffect(() => {
    const subscription = watch((value, info) => {
      if (!isResettingRef.current && value.siteRates) {
        setSiteRates(value.siteRates as SiteRateScheduleDTO[]);
        onDirtyStatusChange?.();
      }
      if (!isResettingRef.current && onClearError && info?.name) {
        const match = info.name.match(/^siteRates\.(\d+)\b/);
        if (match) {
          const idx = Number(match[1]);
          const editedRate = (value.siteRates ?? [])[idx] as
            | SiteRateScheduleDTO
            | undefined;
          if (editedRate?.clientKey) {
            onClearError(editedRate.clientKey);
          }
        }
      }
      if (!isResettingRef.current && info?.name) {
        const fieldMatch = info.name.match(/^siteRates\.(\d+)\.([^.]+)/);
        const trackedField = fieldMatch
          ? TRACKED_RATE_CARD_FIELDS[fieldMatch[2]]
          : undefined;
        if (fieldMatch && trackedField) {
          const editedRate = (value.siteRates ?? [])[Number(fieldMatch[1])] as
            | SiteRateScheduleDTO
            | undefined;
          const dedupeKey = `${editedRate?.clientKey ?? fieldMatch[1]}:${trackedField}`;
          if (!trackedFieldEditsRef.current.has(dedupeKey)) {
            trackedFieldEditsRef.current.add(dedupeKey);
            AnalyticsController.track('rate_card_field_edited', {
              scope,
              field: trackedField,
            });
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setSiteRates, onDirtyStatusChange, onClearError, scope]);

  const handleDuplicate = useCallback(
    (index: number) => {
      const watchedList = getValues('siteRates');
      const currentItem = watchedList[index];

      const duplicated: SiteRateScheduleDTO = {
        ...currentItem,
        id: undefined,
        // TRE-14560: a duplicate is a brand-new record from the resilient
        // save flow's perspective — it must have its own clientKey so the
        // POST result lands on the right row, not the source's row.
        clientKey: crypto.randomUUID(),
        // Re-resolve the "use vendor rate for driver" default: the source's
        // value was resolved against a card that had an id, and the resolver
        // only applies the feature-flag default to unsaved cards. Spreading it
        // verbatim would leave a brand-new card carrying the saved card's
        // answer. Clearing the inherited value matters as much as clearing the
        // id — every card in form state already holds a concrete boolean, so
        // the resolver would otherwise return it untouched and never reach the
        // feature-flag branch.
        useVendorRateForDriver: resolveDefault({
          ...currentItem,
          id: undefined,
          useVendorRateForDriver: undefined,
        }),
        customerAddOns: (currentItem.customerAddOns ?? []).map(
          (addOn: AddOnAssignmentSchema) => ({
            ...addOn,
            isNew: true,
          }),
        ),
        vendorAddOns: (currentItem.vendorAddOns ?? []).map(
          (addOn: AddOnAssignmentSchema) => ({
            ...addOn,
            isNew: true,
          }),
        ),
        driverAddOns: (currentItem.driverAddOns ?? []).map(
          (addOn: AddOnAssignmentSchema) => ({
            ...addOn,
            isNew: true,
          }),
        ),
      };

      append(duplicated);
      // Distinct from `rate_card_added` (the add-button funnel start) — a
      // duplicate is its own affordance. Both land in `created_count` on
      // `rate_cards_submitted` either way.
      AnalyticsController.track('rate_card_duplicated', { scope });
    },
    [getValues, append, resolveDefault, scope],
  );

  // Star, unstar, and delete-while-starred all move rates onto downstream
  // jobs, so we defer the actual mutation until the user confirms. While set,
  // no cascade is enqueued; cancel just clears the pending change so nothing
  // moves and the rate state stays as-is.
  const [pendingRateChange, setPendingRateChange] = useState<PendingRateChange | null>(
    null,
  );

  // clientKey of the row whose delete button just triggered an affected-orders
  // fetch. Used to disable the icon for the ~200-500ms it takes the BE to
  // reply, so the user doesn't double-click into a duplicate fetch.
  // Keyed by clientKey (not index) so a parent re-seed during the fetch
  // doesn't strand the disabled state on the wrong row (TRE-14609 × TRE-14560).
  const [fetchingDeleteClientKey, setFetchingDeleteClientKey] = useState<string | null>(
    null,
  );

  // Soft-delete the SRS identified by `clientKey`. Saved SRS are flagged so the
  // parent's save-time diff fires the DELETE; unsaved rows are removed outright.
  // No-ops if the row is gone (e.g. removed server-side between dialog open and
  // confirm). Centralized so the immediate path and the confirm-then-delete
  // paths share one source of truth.
  const performDelete = useCallback(
    (clientKey: string) => {
      const watchedList = getValues('siteRates') as SiteRateScheduleDTO[];
      const index = watchedList.findIndex((rate) => rate.clientKey === clientKey);
      if (index === -1) return;
      const currentItem = watchedList[index];
      const isAlreadySaved = Boolean(currentItem.id);

      // Match by id or clientKey. On Copy & Edit, the starred row may
      // still be unsaved (server id stripped) and tracked through its
      // clientKey; deleting it must clear the parent's starred ref the
      // same way as a saved-row delete would.
      if (
        (currentItem.id && currentItem.id === defaultSiteRateScheduleId) ||
        (currentItem.clientKey && currentItem.clientKey === defaultSiteRateScheduleId)
      ) {
        onDefaultSiteRateScheduleChange?.(null);
      }

      if (isAlreadySaved) {
        const updatedItem = { ...currentItem, isDeleted: true };
        update(index, updatedItem);
        // TRE-14602: when this is the starred rate, the cascade
        // `onDefaultSiteRateScheduleChange?.(null)` above changes parent
        // state. The parent (NewOrderForm via FreightRates) re-renders
        // and passes new inline-arrow callback refs back down. Our
        // `watch` subscription useEffect has those refs in its deps, so
        // it tears down + resubscribes — and the `update()` watch event
        // can race past the new subscription, leaving the parent's
        // `siteRates` stale and `toDelete` empty at save time (no DELETE
        // on the SRS). Push the updated array up synchronously to
        // bypass the watch path so the DELETE fires on save.
        const updatedList = watchedList.map((rate, i) =>
          i === index ? updatedItem : rate,
        );
        setSiteRates(updatedList);
      } else {
        remove(index);
      }
    },
    [
      getValues,
      update,
      remove,
      setSiteRates,
      defaultSiteRateScheduleId,
      onDefaultSiteRateScheduleChange,
    ],
  );

  const handleDelete = useCallback(
    async (index: number) => {
      const watchedList = getValues('siteRates') as SiteRateScheduleDTO[];
      const currentItem = watchedList[index];
      const isAlreadySaved = Boolean(currentItem.id);
      const clientKey = currentItem.clientKey;

      // Order scope: confirm only when removing the starred default. The
      // copy mirrors the unstar dialog because deletion produces the same
      // downstream cascade (TRE-14609).
      if (
        scope === 'order' &&
        isAlreadySaved &&
        currentItem.id === defaultSiteRateScheduleId &&
        clientKey
      ) {
        setPendingRateChange({ type: 'delete-order-default', clientKey });
        return;
      }

      // Project scope: if any non-completed orders currently default to this
      // SRS, list them in a confirmation modal so the user knows their job
      // rates will be rematched. Unsaved rows can't have any
      // referencing orders, so skip the fetch.
      if (
        scope === 'project' &&
        isAlreadySaved &&
        companyId &&
        currentItem.id &&
        clientKey
      ) {
        // With the selective-rate-apply toggle ON, the delete is gated by the
        // merged save-time impact dialog in FreightRates (the soft-deleted row flows into
        // `toDelete` and is previewed there against the cascade-reachable audience). Skip the
        // click-time read-only warning so one save shows exactly one dialog; the
        // actual DELETE still fires from the parent's save-time diff, unchanged.
        if (applyRateChangesToExistingOrders) {
          performDelete(clientKey);
          return;
        }

        setFetchingDeleteClientKey(clientKey);
        let affectedOrders: Order_Read_Typeahead[];
        try {
          affectedOrders = await getSiteRateScheduleDefaultOnOrders(
            companyId,
            currentItem.id as string,
          );
        } catch {
          // The safety lookup failed (toast already surfaced by the
          // hook). Fail-closed — abandon the delete so the user can retry
          // once the network recovers, rather than silently losing the rate
          // card without seeing which orders would be affected.
          return;
        } finally {
          setFetchingDeleteClientKey(null);
        }

        if (affectedOrders.length > 0) {
          setPendingRateChange({ type: 'delete-project', clientKey, affectedOrders });
          return;
        }

        // Re-resolve the current index in case the form was re-seeded during
        // the fetch. performDelete handles
        // the lookup, so we can delegate by clientKey.
        performDelete(clientKey);
        return;
      }

      // Fall-through path for unsaved rows and non-starred order-scope rows:
      // no fetch, no dialog, no re-seed window, so the index is still valid.
      // performDelete still resolves by clientKey when present.
      if (clientKey) {
        performDelete(clientKey);
      } else {
        // Defensive: rows without a clientKey shouldn't exist after the form
        // mounts, but if one slips through, remove by index immediately.
        remove(index);
      }
    },
    [
      getValues,
      scope,
      companyId,
      applyRateChangesToExistingOrders,
      defaultSiteRateScheduleId,
      getSiteRateScheduleDefaultOnOrders,
      performDelete,
      remove,
    ],
  );

  // Hold the most-recently-shown dialog type so the title/body/button copy
  // stays stable while the Fade transition closes. Reading directly from
  // `pendingRateChange` would flip every ternary to the default ("star")
  // branch the instant we clear it on confirm/cancel, flashing the wrong
  // copy during the ~225ms fade-out.
  const lastShownTypeRef = useRef<PendingRateChange['type']>('star');
  // Same idea for the project-delete affected-orders list: keep the last
  // rendered list so the bullets don't vanish mid-fade after confirm/cancel.
  const lastShownAffectedOrdersRef = useRef<Order_Read_Typeahead[]>([]);
  if (pendingRateChange) {
    lastShownTypeRef.current = pendingRateChange.type;
    if (pendingRateChange.type === 'delete-project') {
      lastShownAffectedOrdersRef.current = pendingRateChange.affectedOrders;
    }
  }
  const dialogType = lastShownTypeRef.current;

  const handleToggleDefault = useCallback(
    (index: number) => {
      const watchedList = getValues('siteRates');
      const currentItem = watchedList[index];
      const currentId = currentItem?.id as string | undefined;
      // On Copy & Edit, SRS rows can have their server id stripped (the
      // SRS will be recreated on save). The form's starred reference
      // rides on `clientKey` until the SRS POST lands and the order
      // form PATCHes the resolved server id in. On regular Edit, a
      // row without a server id is a freshly-added unsaved row and
      // can't be starred (the PATCH-on-save flow doesn't defer the
      // order update there), so star/unstar stays gated on a real id.
      const currentKey = currentItem?.clientKey as string | undefined;

      if (currentId && defaultSiteRateScheduleId === currentId) {
        setPendingRateChange({ type: 'unstar' });
      } else if (!currentId && currentKey && defaultSiteRateScheduleId === currentKey) {
        setPendingRateChange({ type: 'unstar' });
      } else if (currentId) {
        setPendingRateChange({ type: 'star', rateId: currentId });
      } else if (isCopyAndEdit && currentKey) {
        setPendingRateChange({ type: 'star', rateId: currentKey });
      }
    },
    [getValues, defaultSiteRateScheduleId, isCopyAndEdit],
  );

  const handleConfirmRateChange = useCallback(() => {
    if (pendingRateChange?.type === 'star') {
      if (onDefaultSiteRateScheduleChange) {
        onDefaultSiteRateScheduleChange(pendingRateChange.rateId);
        // Confirmed step, not the star click — the click only opens the
        // dialog, and the event fires only when the change actually applied.
        // Star/unstar is an order-scope-only affordance (hidden on projects),
        // so `scope` is 'order' in practice; carried for event-shape parity.
        AnalyticsController.track('rate_card_star_toggled', { scope, starred: true });
      }
    } else if (pendingRateChange?.type === 'unstar') {
      if (onDefaultSiteRateScheduleChange) {
        onDefaultSiteRateScheduleChange(null);
        AnalyticsController.track('rate_card_star_toggled', { scope, starred: false });
      }
    } else if (
      pendingRateChange?.type === 'delete-order-default' ||
      pendingRateChange?.type === 'delete-project'
    ) {
      // performDelete resolves the current index from clientKey, so a parent
      // re-seed between dialog open and confirm won't mis-target.
      performDelete(pendingRateChange.clientKey);
    }
    setPendingRateChange(null);
  }, [pendingRateChange, onDefaultSiteRateScheduleChange, performDelete, scope]);

  const handleCancelRateChange = useCallback(() => {
    setPendingRateChange(null);
  }, []);

  // Selective-rate-apply: with the toggle ON, a card delete's skip selection is collected by the
  // MERGED save-time impact dialog in FreightRates (the soft-deleted row flows into `toDelete`) and
  // forwarded onto the DELETE cascade by useSiteRateScheduleOperations. This handler is the
  // toggle-OFF click-time path, where the cascade applies to ALL reachable orders by design (no
  // selective skip when the toggle is off — the BE leaves the skip set empty), so there are no skips to
  // plumb here.
  const handleConfirmProjectDeleteWithSkips = useCallback(
    // TRE-16125: the drawer reports a structured confirm result (skip set + the referenced-list set).
    // This delete-card flow renders the drawer WITHOUT the referenced model (no `isReferencedModel`
    // prop), so it never durably dereferences and never pops the full-dereference confirm — the
    // toggle-OFF delete path applies to ALL reachable orders by design, so the payload is unused.
    (result: RateChangeImpactConfirmResult) => {
      void result;
      handleConfirmRateChange();
    },
    [handleConfirmRateChange],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Compute display numbers (sequential for non-deleted items)
  const freightRateCoverageError =
    !isFreightRateRequired || hasCustomerFreightRate(siteRates)
      ? undefined
      : String(errors[FREIGHT_RATE_COVERAGE_ERROR_PATH]?.message ?? '');

  let displayNumber = 0;
  // The "1" badge on a lone rate card reads as meaningless noise (there's
  // nothing to number it against) — only show numbering once a second
  // card exists to disambiguate.
  const visibleCardCount = fields.filter((field, index) => {
    const currentItem = getValues('siteRates')[index];
    const cardError = currentItem?.clientKey
      ? errorsByClientKey?.get(currentItem.clientKey)
      : undefined;

    return !(currentItem?.isDeleted && !cardError?.rateError);
  }).length;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h6">{t('project.form.freight_rates')}</Typography>
      </Box>
      {/* Rate items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {fields.map((field, index) => {
          const watchedList = getValues('siteRates');
          const currentItem = watchedList[index];

          const cardError = currentItem?.clientKey
            ? errorsByClientKey?.get(currentItem.clientKey)
            : undefined;

          // Keep failed-delete rows visible — when the user
          // marked the row for delete and the DELETE call failed, we render
          // it as "marked for retry on next submit" rather than rolling the
          // deletion back. Successful deletes
          // are dropped from siteRates by `applySaveResult`, so a still-
          // visible row here means we should retry next submit.
          if (currentItem?.isDeleted && !cardError?.rateError) {
            return null;
          }

          displayNumber += 1;

          return (
            <FreightRateItem
              key={field.id}
              control={control}
              setValue={setValue}
              index={index}
              displayNumber={displayNumber}
              showDisplayNumber={visibleCardCount > 1}
              onDuplicate={() => handleDuplicate(index)}
              onDelete={() => handleDelete(index)}
              accountId={accountId}
              projectId={projectId}
              prevailingWage={prevailingWage}
              orderDate={orderDate}
              scope={scope}
              companyId={companyId}
              error={cardError}
              isDefault={
                // The starred/default rate is an order-only
                // concept (`orders.default_site_rate_schedule_id`); the
                // project surface must never show it.
                scope !== 'project' &&
                // On Copy & Edit with stripped SRS ids the server `id`
                // is undefined until the SRS POST lands, but `clientKey`
                // carries the form's re-pointed starred reference so
                // the badge still renders on the right row.
                ((Boolean(currentItem?.id) &&
                  currentItem?.id === defaultSiteRateScheduleId) ||
                  (Boolean(currentItem?.clientKey) &&
                    currentItem?.clientKey === defaultSiteRateScheduleId))
              }
              onToggleDefault={
                // No star toggle on the project surface —
                // omitting the handler hides the star UI entirely in
                // FreightRateItem.
                scope !== 'project' &&
                onDefaultSiteRateScheduleChange &&
                // Existing rows always offer the star toggle. On the
                // Copy & Edit form rows can also be starred by their
                // `clientKey` — the order form defers the order POST
                // and resolves clientKey → new server id after SRS
                // save. On regular Edit, a row without a server id is
                // a freshly-added unsaved row and gets no toggle.
                (currentItem?.id ||
                  (isCopyAndEdit && currentItem?.clientKey) ||
                  currentItem?.clientKey === defaultSiteRateScheduleId)
                  ? () => handleToggleDefault(index)
                  : undefined
              }
              isDeletePending={
                Boolean(currentItem?.clientKey) &&
                fetchingDeleteClientKey === currentItem?.clientKey
              }
            />
          );
        })}
      </Box>
      {Boolean(freightRateCoverageError) && (
        <Typography variant="body2" sx={{ color: theme.palette.brandV2Red.main, mt: 1 }}>
          {freightRateCoverageError}
        </Typography>
      )}
      {/* Add another rate button */}
      <ButtonBase
        onClick={handleAddFreightRule}
        sx={{
          width: '100%',
          mt: 1,
          p: 2,
          // treadGray5, matching "Add condition" inside each rate card —
          // treadGray7 (the app's default border weight) was nearly
          // invisible against this section's background.
          border: `2px solid ${theme.brandV2.colors.treadGray5}`,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Add sx={{ fontSize: 20, color: theme.semantic.linkText }} />
        <Typography
          variant="body2"
          sx={{ color: theme.semantic.linkText, fontWeight: 500 }}
        >
          {t('project.form.add_another_rate')}
        </Typography>
      </ButtonBase>
      {dialogType === 'delete-project' ? (
        <RateChangeImpactDrawer
          isOpen={pendingRateChange !== null}
          scope={scope}
          orders={lastShownAffectedOrdersRef.current.map((order) => ({
            id: order.id,
            orderNumber: order.order_id,
            // state/load_at are present in the response payload but not yet typed on
            // Order_Read_Typeahead (api-spec pending) — read them defensively.
            state: (order as { state?: string }).state,
            loadAt: (order as { load_at?: string }).load_at,
          }))}
          title={t('project.form.delete_default_rate_card_title')}
          intro={t('project.form.delete_default_rate_card_intro')}
          warning={t('project.form.delete_default_rate_card_warning')}
          confirmLabel={t('project.form.delete_default_rate_card_confirm')}
          confirmColor="brandV2Red"
          // The delete flow has no date range (`showDateRange` stays false so the picker
          // never renders and this is never called); resolve the current orders to satisfy
          // the required prop (TRE-15583).
          fetchOrders={() =>
            Promise.resolve(
              lastShownAffectedOrdersRef.current.map((order) => ({
                id: order.id,
                orderNumber: order.order_id,
                state: (order as { state?: string }).state,
                loadAt: (order as { load_at?: string }).load_at,
              })),
            )
          }
          onConfirm={handleConfirmProjectDeleteWithSkips}
          onClose={handleCancelRateChange}
        />
      ) : (
        <ConfirmationDialog
          isOpen={pendingRateChange !== null}
          onClose={handleCancelRateChange}
          onConfirm={handleConfirmRateChange}
          title={dialogTitleFor(dialogType)}
          confirmButtonProps={{
            color: dialogType === 'star' ? 'primary' : 'brandV2Red',
            children: dialogConfirmLabelFor(dialogType),
          }}
        >
          <Typography>{dialogBodyFor(dialogType)}</Typography>
        </ConfirmationDialog>
      )}
    </Box>
  );
};

function dialogTitleFor(type: PendingRateChange['type']): string {
  if (type === 'unstar' || type === 'delete-order-default') {
    return t('order.form_v2.remove_default_rate_confirmation_title');
  }
  if (type === 'delete-project') {
    return t('project.form.delete_default_rate_card_title');
  }
  return t('order.form_v2.set_default_rate_confirmation_title');
}

function dialogConfirmLabelFor(type: PendingRateChange['type']): string {
  if (type === 'unstar' || type === 'delete-order-default') {
    return t('order.form_v2.remove_default_rate_confirmation_confirm');
  }
  if (type === 'delete-project') {
    return t('project.form.delete_default_rate_card_confirm');
  }
  return t('order.form_v2.set_default_rate_confirmation_confirm');
}

function dialogBodyFor(type: PendingRateChange['type']): string {
  if (type === 'unstar' || type === 'delete-order-default') {
    return t('order.form_v2.remove_default_rate_confirmation_message');
  }
  return t('order.form_v2.set_default_rate_confirmation_message');
}

export default FreightRatesManager;
